import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { hasEConsent, checkConsentWithApi, getConsentStatus, purgeLegacyConsentStorage } from "@/lib/consent";

/**
 * E-Consent gate — redirects to /e-consent if the user hasn't consented yet.
 *
 * Behaviour:
 * - First login after registration → show E-Consent page
 * - After consenting → never shown again for the session; every new session
 *   re-asks the API (NO browser persistence — the API is the only source of
 *   truth, so cross-device/browser logins are always correct).
 *
 * Flow:
 * 1. Check the in-memory session store (instant once the API answered).
 * 2. If unknown → ask the API once per user session.
 * 3. While the API call is in-flight, do NOT redirect (prevent race-condition flash).
 * 4. Once the API responds, record the answer in the in-memory session store
 *    and redirect accordingly (no browser persistence).
 */
export const EConsentGate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Track whether the one-time API check has finished so we don't redirect
  // prematurely while the network call is in flight.
  const [verified, setVerified] = useState(false);
  const checkingRef = useRef(false);
  // Track which user we verified for — reset when user changes (logout/login)
  const verifiedForUser = useRef<string | null>(null);

  // Purge legacy e-consent cache keys once — the API is the only source of
  // truth now (per Rock 2026-07-23); a stale cached "accepted" must never
  // bypass the gate. econsent_returnUrl is preserved.
  useEffect(() => {
    purgeLegacyConsentStorage();
  }, []);

  // ── Step 1: verify consent (in-memory store + API) ──
  useEffect(() => {
    if (!user) {
      // User logged out — reset verification so next login starts fresh
      setVerified(false);
      verifiedForUser.current = null;
      checkingRef.current = false;
      return;
    }

    const userId = user.email ?? user.id ?? "";
    if (!userId) return;

    // If user changed (different login), reset verification
    if (verifiedForUser.current !== null && verifiedForUser.current !== userId) {
      setVerified(false);
      verifiedForUser.current = null;
      checkingRef.current = false;
    }

    // Fast path – the in-memory session store already says "yes"
    // (set by a prior API answer or an accept POST this session)
    if (hasEConsent(userId)) {
      verifiedForUser.current = userId;
      setVerified(true);
      return;
    }

    // The former NP-213 mock-mode auto-accept was REMOVED (per Rock,
    // 2026-07-23): the consent API is the ONLY source of truth in every
    // auth mode. In local mock mode the MSW handlers answer the same
    // endpoints, so the flow is identical (accept once per session).

    // The store may already hold a definitive answer for this session —
    // AuthContext.login resolves /borrower/me before exposing the user, so
    // re-calling here would be a duplicate request per login.
    if (getConsentStatus(userId) === 'unconsented') {
      verifiedForUser.current = userId;
      setVerified(true);
      return;
    }

    // Avoid duplicate in-flight API requests
    if (checkingRef.current) return;
    checkingRef.current = true;

    checkConsentWithApi(userId)
      .finally(() => {
        checkingRef.current = false;
        verifiedForUser.current = userId;
        setVerified(true);
      });
  }, [user]);

  // ── Step 2: gate navigation once verification is done ──
  useEffect(() => {
    if (!user || !verified) return;

    const userId = user.email ?? user.id ?? "";
    if (hasEConsent(userId)) {
      // Consent confirmed for this session (in memory) — nothing to do.
      return;
    }

    // R28a: All users see e-consent on first login, regardless of lifecycle state
    // User hasn't consented — redirect unless already on the consent page
    if (location.pathname !== "/e-consent") {
      // Preserve the current location as returnUrl so e-consent can redirect back
      const currentPath = location.pathname + (location.search || '');
      const params = new URLSearchParams();
      if (currentPath && currentPath !== '/' && currentPath !== '/login') {
        params.set('returnUrl', currentPath);
        // Also persist to sessionStorage as a fallback in case the query param
        // is lost during the redirect chain (e.g. race between Login navigate
        // and PublicOnlyRoute Navigate).
        try { sessionStorage.setItem('econsent_returnUrl', currentPath); } catch { /* sessionStorage unavailable */ }
      }
      const search = params.toString();
      navigate(`/e-consent${search ? `?${search}` : ''}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate, user, verified]);

  return null;
};
